import { useState, useEffect, useCallback } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { finnGetSummary, finnUpdateRule, finnGetOutbox, finnApprove, finnReject, finnGetActivity } from '../../lib/adminApi';

const RULE_ICONS = {
  no_upload_24h:   '📭',
  uploaded_no_pay: '💳',
  trial_went_quiet:'🎯',
  inactive_paying: '😴',
  job_failed:      '💥',
};

const RULE_COLORS = {
  no_upload_24h:   '#f59e0b',
  uploaded_no_pay: '#F37428',
  trial_went_quiet:'#6c63ff',
  inactive_paying: '#94a3b8',
  job_failed:      '#ef4444',
};

export default function Finn() {
  const [summary, setSummary]     = useState(null);
  const [outbox, setOutbox]       = useState([]);
  const [activity, setActivity]   = useState([]);
  const [loading, setLoading]     = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview | outbox | activity
  const [outboxTab, setOutboxTab] = useState('pending');
  const [previewItem, setPreviewItem] = useState(null);
  const [togglingRule, setTogglingRule] = useState(null);
  const [editingRule, setEditingRule]   = useState(null);
  const [editGuidance, setEditGuidance] = useState('');
  const [approvingId, setApprovingId]   = useState(null);

  const load = useCallback(async () => {
    try {
      const [s, o, a] = await Promise.all([
        finnGetSummary(),
        finnGetOutbox('pending'),
        finnGetActivity(30),
      ]);
      setSummary(s);
      setOutbox(o?.items || []);
      setActivity(a?.items || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function loadOutboxTab(tab) {
    setOutboxTab(tab);
    try {
      const o = await finnGetOutbox(tab);
      setOutbox(o?.items || []);
    } catch {}
  }

  async function toggleRule(rule) {
    setTogglingRule(rule.rule_key);
    try {
      await finnUpdateRule(rule.rule_key, { is_active: !rule.is_active });
      setSummary(s => ({
        ...s,
        rules: s.rules.map(r => r.rule_key === rule.rule_key ? { ...r, is_active: !r.is_active } : r),
      }));
    } catch {} finally { setTogglingRule(null); }
  }

  async function saveGuidance() {
    try {
      await finnUpdateRule(editingRule.rule_key, { email_guidance: editGuidance });
      setSummary(s => ({
        ...s,
        rules: s.rules.map(r => r.rule_key === editingRule.rule_key ? { ...r, email_guidance: editGuidance } : r),
      }));
      setEditingRule(null);
    } catch (e) { alert(e.message); }
  }

  async function approve(item) {
    setApprovingId(item.outbox_id);
    try {
      await finnApprove(item.outbox_id);
      setOutbox(o => o.filter(x => x.outbox_id !== item.outbox_id));
      setSummary(s => ({ ...s, pending: Math.max(0, (s.pending || 1) - 1), emails_sent: (s.emails_sent || 0) + 1 }));
      setPreviewItem(null);
    } catch (e) { alert(e.message); } finally { setApprovingId(null); }
  }

  async function reject(item) {
    try {
      await finnReject(item.outbox_id);
      setOutbox(o => o.filter(x => x.outbox_id !== item.outbox_id));
      setSummary(s => ({ ...s, pending: Math.max(0, (s.pending || 1) - 1) }));
      setPreviewItem(null);
    } catch (e) { alert(e.message); }
  }

  function timeAgo(dateStr) {
    if (!dateStr) return '—';
    const diff = Date.now() - new Date(dateStr).getTime();
    const m = Math.floor(diff / 60000);
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    return `${Math.floor(h / 24)}d ago`;
  }

  const rules = summary?.rules || [];
  const pending = summary?.pending || 0;

  return (
    <div style={{ display: 'flex', minHeight: '100vh', background: '#f0f5fb',
      fontFamily: "'Inter', system-ui, sans-serif" }}>
      <AdminSidebar active="finn" />
      <main style={{ flex: 1, overflowY: 'auto' }}>
      {loading ? <div style={s.loading}>Loading Finn…</div> : <>

      {/* ── Hero Banner ── */}
      <div style={{
        background: 'linear-gradient(135deg, #0a2540 0%, #0f3460 50%, #162d4a 100%)',
        padding: '32px 36px 28px', position: 'relative', overflow: 'hidden',
      }}>
        <div style={{ position: 'absolute', inset: 0, opacity: 0.04,
          backgroundImage: 'repeating-linear-gradient(0deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px),repeating-linear-gradient(90deg,#fff 0px,#fff 1px,transparent 1px,transparent 40px)' }} />
        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 18 }}>
            <div style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(243,116,40,0.15)',
              border: '1px solid rgba(243,116,40,0.3)', display: 'flex', alignItems: 'center',
              justifyContent: 'center', fontSize: 26, flexShrink: 0 }}>🐾</div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: '#fff' }}>Finn</h1>
                <span style={{ background: 'rgba(243,116,40,0.2)', color: '#F37428', fontSize: 9,
                  fontWeight: 700, letterSpacing: '0.1em', padding: '3px 8px', borderRadius: 10,
                  textTransform: 'uppercase', border: '1px solid rgba(243,116,40,0.3)' }}>Bot 1</span>
              </div>
              <p style={{ margin: 0, color: 'rgba(255,255,255,0.6)', fontSize: 13, maxWidth: 520, lineHeight: 1.5 }}>
                Customer success · Email nudges · Approval queue
              </p>
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8, flexShrink: 0 }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'right' }}>
              Last run: <span style={{ color: 'rgba(255,255,255,0.85)', fontWeight: 600 }}>
                {summary?.last_run ? new Date(summary.last_run).toLocaleString() : 'Never'}
              </span>
            </div>
            <span style={{ background: 'rgba(52,211,153,0.15)', color: '#34d399', fontSize: 11,
              fontWeight: 700, padding: '4px 12px', borderRadius: 10,
              border: '1px solid rgba(52,211,153,0.3)' }}>✓ Active</span>
          </div>
        </div>
      </div>

      <div style={s.page}>

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 22 }}>
        {[
          { label: 'Emails sent (7d)', val: summary?.emails_sent || 0, color: '#0f172a' },
          { label: 'Nudges converted', val: summary?.converted || 0, color: '#10b981' },
          { label: 'Pending approval', val: pending, color: pending > 0 ? '#F37428' : '#0f172a', onClick: pending > 0 ? () => { setActiveTab('outbox'); loadOutboxTab('pending'); } : undefined },
          { label: 'Errors', val: summary?.errors || 0, color: summary?.errors > 0 ? '#ef4444' : '#0f172a' },
          { label: 'Next run', val: '6:00 AM UTC', color: '#64748b', small: true },
        ].map(({ label, val, color, onClick, small }) => (
          <div key={label} style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
            <div style={{ fontSize: small ? 14 : 24, fontWeight: 700, color, lineHeight: 1 }}>{val}</div>
            <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
          </div>
        ))}
      </div>

      {/* ── Tabs ── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 24,
        background: '#e2e8f0', borderRadius: 10, padding: 4, width: 'fit-content' }}>
        {[['overview','Overview'],['outbox', `Pending Approval${pending > 0 ? ` (${pending})` : ''}`],['activity','Activity Log']].map(([key, label]) => (
          <button key={key} onClick={() => setActiveTab(key)} style={{
            padding: '7px 18px', border: 'none', borderRadius: 8, fontSize: 13,
            fontWeight: activeTab === key ? 700 : 500, cursor: 'pointer',
            background: activeTab === key ? '#fff' : 'transparent',
            color: activeTab === key ? '#0a2540' : '#64748b',
            boxShadow: activeTab === key ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            transition: 'all 0.15s',
          }}>{label}</button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ── */}
      {activeTab === 'overview' && (
        <div>
          <div style={s.sectionHeader}>
            <div style={s.sectionTitle}>Watch List</div>
            <div style={s.sectionSub}>Finn monitors these patterns daily and acts automatically</div>
          </div>
          <div style={s.grid}>
            {rules.map(rule => (
              <div key={rule.rule_key} style={{ ...s.card, borderTop: `3px solid ${rule.is_active ? (RULE_COLORS[rule.rule_key] || '#6c63ff') : '#e2e8f0'}` }}>
                <div style={s.cardHeader}>
                  <span style={{ fontSize: 24 }}>{RULE_ICONS[rule.rule_key] || '🤖'}</span>
                  <Toggle on={rule.is_active} loading={togglingRule === rule.rule_key} onToggle={() => toggleRule(rule)} />
                </div>
                <div style={{ ...s.cardTitle, color: rule.is_active ? '#0f172a' : '#94a3b8' }}>{rule.rule_name}</div>
                <div style={{ ...s.cardDesc, color: rule.is_active ? '#64748b' : '#cbd5e1' }}>{rule.description}</div>
                <div style={s.cardAction}>
                  <strong>Fires after:</strong> {rule.trigger_hours === 0 ? 'Immediately' : `${rule.trigger_hours}h`}
                  {' · '}
                  <strong>Send:</strong> {rule.auto_send ? 'Auto' : 'Approval required'}
                </div>
                <div style={s.cardFooter}>
                  <div>
                    <div style={{ ...s.cardCount, color: rule.is_active ? '#0f172a' : '#cbd5e1' }}>
                      {rule.fired_this_week || 0}
                    </div>
                    <div style={s.cardCountLabel}>fired this week</div>
                  </div>
                  <button style={s.btnSm} onClick={() => { setEditingRule(rule); setEditGuidance(rule.email_guidance || ''); }}>
                    Edit guidance
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── OUTBOX TAB ── */}
      {activeTab === 'outbox' && (
        <div>
          <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
            {['pending','sent','rejected'].map(tab => (
              <button key={tab} style={{ ...s.tab, ...(outboxTab === tab ? s.tabActive : {}) }}
                onClick={() => loadOutboxTab(tab)}>
                {tab.charAt(0).toUpperCase() + tab.slice(1)}
              </button>
            ))}
          </div>

          {outbox.length === 0 ? (
            <div style={s.empty}>
              {outboxTab === 'pending' ? '🐾 Nothing waiting — Finn is all caught up.' : `No ${outboxTab} emails.`}
            </div>
          ) : (
            <div style={s.feedCard}>
              {outbox.map(item => (
                <div key={item.outbox_id} style={s.feedRow} onClick={() => setPreviewItem(item)}>
                  <div style={{ ...s.feedIcon, background: '#eff6ff' }}>✉️</div>
                  <div style={{ flex: 1 }}>
                    <div style={s.feedText}>
                      <strong>{item.to_name}</strong>
                      <span style={s.tenantChip}>{item.rule_name}</span>
                    </div>
                    <div style={s.feedMeta}>{item.to_email} · {timeAgo(item.created_at)}</div>
                  </div>
                  {outboxTab === 'pending' && (
                    <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
                      <button style={s.btnApprove} onClick={() => approve(item)} disabled={approvingId === item.outbox_id}>
                        {approvingId === item.outbox_id ? '…' : '✓ Send'}
                      </button>
                      <button style={s.btnReject} onClick={() => reject(item)}>✕</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── ACTIVITY TAB ── */}
      {activeTab === 'activity' && (
        <div>
          {activity.length === 0 ? (
            <div style={s.empty}>No activity yet — Finn hasn't run.</div>
          ) : (
            <div style={s.feedCard}>
              {activity.map(item => (
                <div key={item.log_id} style={s.feedRow}>
                  <div style={{ ...s.feedIcon, background: item.action === 'email_sent' ? '#eff6ff' : item.action === 'converted' ? '#f0fdf4' : item.action === 'flagged' ? '#fffbeb' : '#f8fafc' }}>
                    {item.action === 'email_sent' ? '✉️' : item.action === 'converted' ? '✅' : item.action === 'flagged' ? '🚩' : '📋'}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={s.feedText}>{item.description}</div>
                    <div style={s.feedMeta}>
                      {item.company_name && <span style={s.tenantChip}>{item.company_name}</span>}
                      {item.rule_key}
                    </div>
                  </div>
                  <div style={s.feedTime}>{timeAgo(item.created_at)}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── Email preview modal ── */}
      {previewItem && (
        <div style={s.modalOverlay} onClick={() => setPreviewItem(null)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>Email Preview</div>
                <div style={s.modalSub}>To: {previewItem.to_name} &lt;{previewItem.to_email}&gt;</div>
                <div style={s.modalSub}>Subject: {previewItem.subject}</div>
              </div>
              <button style={s.modalClose} onClick={() => setPreviewItem(null)}>✕</button>
            </div>
            <div style={s.modalBody}>
              <iframe
                srcDoc={previewItem.html_body}
                style={{ width: '100%', height: 420, border: 'none', borderRadius: 8, background: '#f5f6f8' }}
                title="Email preview"
              />
            </div>
            {previewItem.status === 'pending' && (
              <div style={s.modalFooter}>
                <button style={s.btnReject} onClick={() => reject(previewItem)}>✕ Reject</button>
                <button style={s.btnApprove} onClick={() => approve(previewItem)} disabled={approvingId === previewItem.outbox_id}>
                  {approvingId === previewItem.outbox_id ? 'Sending…' : '✓ Approve & Send'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Guidance edit modal ── */}
      {editingRule && (
        <div style={s.modalOverlay} onClick={() => setEditingRule(null)}>
          <div style={{ ...s.modal, maxWidth: 580 }} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>{RULE_ICONS[editingRule.rule_key]} {editingRule.rule_name}</div>
                <div style={s.modalSub}>Edit the guidance Finn uses to write this email with Claude</div>
              </div>
              <button style={s.modalClose} onClick={() => setEditingRule(null)}>✕</button>
            </div>
            <div style={{ padding: '0 24px 16px' }}>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 500 }}>EMAIL SUBJECT</div>
              <div style={{ fontSize: 14, color: '#0f172a', padding: '10px 14px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16 }}>
                {editingRule.email_subject}
              </div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6, fontWeight: 500 }}>GUIDANCE FOR CLAUDE</div>
              <textarea
                style={{ width: '100%', minHeight: 160, padding: '12px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#f8fafc', resize: 'vertical', boxSizing: 'border-box', fontFamily: 'inherit', lineHeight: 1.6 }}
                value={editGuidance}
                onChange={e => setEditGuidance(e.target.value)}
              />
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                This is the prompt Finn passes to Claude. Plain English. Be specific about tone, length, and what to include.
              </div>
            </div>
            <div style={s.modalFooter}>
              <button style={s.btnSm} onClick={() => setEditingRule(null)}>Cancel</button>
              <button style={s.btnPrimary} onClick={saveGuidance}>Save guidance</button>
            </div>
          </div>
        </div>
      )}

    </div>
    </>}
      </main>
    </div>
  );
}

function Toggle({ on, loading, onToggle }) {
  return (
    <div onClick={loading ? undefined : onToggle} style={{
      width: 36, height: 20, borderRadius: 10, background: on ? '#F37428' : '#e2e8f0',
      position: 'relative', cursor: loading ? 'wait' : 'pointer', flexShrink: 0, transition: 'background 0.2s',
    }}>
      <div style={{
        position: 'absolute', width: 16, height: 16, background: '#fff', borderRadius: '50%',
        top: 2, left: on ? 18 : 2, transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </div>
  );
}

function Stat({ num, label, highlight, highlightColor }) {
  return (
    <div style={{ textAlign: 'center' }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: highlight ? (highlightColor || '#F37428') : '#0f172a', lineHeight: 1 }}>{num}</div>
      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
    </div>
  );
}

const s = {
  page:         { padding: '24px 36px 100px', maxWidth: 1100 },
  loading:      { color: '#94a3b8', fontSize: 14, padding: 40, textAlign: 'center' },
  sectionHeader:{ marginBottom: 14 },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#0f172a' },
  sectionSub:   { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  grid:         { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14 },
  card:         { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 20 },
  cardHeader:   { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 12 },
  cardTitle:    { fontSize: 13, fontWeight: 700, marginBottom: 4 },
  cardDesc:     { fontSize: 12, lineHeight: 1.5, marginBottom: 12 },
  cardAction:   { fontSize: 11, color: '#94a3b8', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '6px 10px', marginBottom: 14 },
  cardFooter:   { display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' },
  cardCount:    { fontSize: 24, fontWeight: 700, lineHeight: 1 },
  cardCountLabel:{ fontSize: 11, color: '#94a3b8', marginTop: 2 },
  feedCard:     { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden' },
  feedRow:      { display: 'flex', alignItems: 'center', gap: 14, padding: '13px 20px', borderBottom: '1px solid #f1f5f9', cursor: 'pointer' },
  feedIcon:     { width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 15, flexShrink: 0 },
  feedText:     { fontSize: 13, color: '#1e293b', lineHeight: 1.4 },
  feedMeta:     { fontSize: 11, color: '#94a3b8', marginTop: 2 },
  feedTime:     { fontSize: 11, color: '#cbd5e1', flexShrink: 0 },
  tenantChip:   { display: 'inline-block', background: '#f1f5f9', color: '#475569', fontSize: 11, fontWeight: 500, padding: '2px 7px', borderRadius: 4, marginLeft: 6 },
  empty:        { padding: '48px 24px', textAlign: 'center', color: '#94a3b8', fontSize: 14, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
  btnPrimary:   { background: '#F37428', color: '#fff', border: 'none', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer' },
  btnSm:        { background: '#f1f5f9', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 500, cursor: 'pointer' },
  btnApprove:   { background: '#22c55e', color: '#fff', border: 'none', borderRadius: 7, padding: '6px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer' },
  btnReject:    { background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 7, padding: '6px 10px', fontSize: 12, cursor: 'pointer' },
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 24 },
  modal:        { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' },
  modalHeader:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '24px 24px 16px' },
  modalTitle:   { fontSize: 16, fontWeight: 700, color: '#0f172a' },
  modalSub:     { fontSize: 12, color: '#94a3b8', marginTop: 3 },
  modalClose:   { background: 'none', border: 'none', color: '#94a3b8', fontSize: 18, cursor: 'pointer', padding: 4 },
  modalBody:    { padding: '0 24px 16px' },
  modalFooter:  { display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '16px 24px', borderTop: '1px solid #f1f5f9' },
};
